import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReportRow } from '../lib/types'
import { formatCellDisplay, getColumnMeta } from './columnMeta'

export type ReportStatus = 'idle' | 'loading' | 'ok' | 'error'

interface CacheEntry {
  rows: ReportRow[]
  fetchedAt: number
}

const CACHE = new Map<string, CacheEntry>()
const CACHE_TTL_MS = 45_000

interface UseReportDataOptions {
  limit?: number
  enabled?: boolean
  skipCache?: boolean
}

interface UseReportDataResult {
  rows: ReportRow[]
  status: ReportStatus
  error: string | null
  fetchedAt: Date | null
  refetch: () => void
}

function cacheKey(id: string, limit: number): string {
  return `${id}:${limit}`
}

export interface ReportDateRange {
  from?: string
  to?: string
}

// range is only honoured by the reports with a date-bounded function twin
// (db/reports_operations.sql section 4) -- passing it for any other report is a 400, and
// omitting it (every existing caller) is byte-identical to the pre-range behaviour.
export async function fetchReport(id: string, limit = 500, range?: ReportDateRange): Promise<ReportRow[]> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (range?.from) params.set('from', range.from)
  if (range?.to) params.set('to', range.to)
  const r = await fetch(`/api/reports/${id}?${params}`)
  const body = (await r.json()) as { rows?: ReportRow[]; error?: string; hint?: string }
  if (!r.ok || body.error) {
    throw new Error(`${body.error ?? r.status}${body.hint ? ` — ${body.hint}` : ''}`)
  }
  return body.rows ?? []
}

export async function fetchReportIndex(): Promise<{ reports: string[]; configured: boolean }> {
  const r = await fetch('/api/reports')
  if (!r.ok) return { reports: [], configured: false }
  return r.json() as Promise<{ reports: string[]; configured: boolean }>
}

export function exportCsv(
  rows: ReportRow[],
  columns: string[],
  filename: string,
  opts?: { applyLoadStateLabels?: boolean },
): void {
  if (!rows.length) return
  const esc = (v: unknown): string => {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const body = [
    columns.join(','),
    ...rows.map((r) => columns.map((c) => {
      const meta = getColumnMeta(c, r[c])
      return esc(formatCellDisplay(r[c], c, meta, opts))
    }).join(',')),
  ]
  // Leading UTF-8 BOM. Without it, Excel has no signal that this file is UTF-8 and
  // falls back to the system codepage (Windows-1252) to decode it -- every blank cell's
  // '—' placeholder (getColumnMeta, below) and every '₹' currency prefix then renders as
  // mojibake ("â€”") the moment the file is opened, even though the web report itself
  // shows them correctly (the browser already knows this page is UTF-8). The BOM is a
  // standard, invisible marker real UTF-8-aware readers strip on their own.
  const url = URL.createObjectURL(new Blob(['\uFEFF' + body.join('\n')], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * A styled Excel workbook (title block, banded table, frozen filtered header, widths,
 * colour-coded severity) built by serving's xlsx_export from the rows already on screen.
 *
 * Numbers go across as numbers so Excel can sum and sort them; everything else goes as the
 * same display text the table shows, so the sheet reads like the screen. [labels] are the
 * human column headings -- without one the server prettifies the key.
 *
 * Throws with the server's own message on failure so the caller can show it.
 */
export async function exportXlsx(
  rows: ReportRow[],
  columns: string[],
  filename: string,
  opts?: {
    title?: string
    subtitle?: string
    labels?: Record<string, string>
    applyLoadStateLabels?: boolean
  },
): Promise<void> {
  if (!rows.length) return
  const body = {
    title: opts?.title ?? filename,
    subtitle: opts?.subtitle,
    filename,
    columns: columns.map((key) => ({ key, label: opts?.labels?.[key] })),
    rows: rows.map((r) => Object.fromEntries(columns.map((c) => {
      const raw = r[c]
      const value = typeof raw === 'number' ? raw : formatCellDisplay(raw, c, getColumnMeta(c, raw), opts)
      return [c, value]
    }))),
  }
  const res = await fetch('/api/reports/export.xlsx', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: string } | null
    throw new Error(err?.error ?? `export failed (${res.status})`)
  }
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}

export function useReportData(
  reportId: string,
  { limit = 500, enabled = true, skipCache = false }: UseReportDataOptions = {},
): UseReportDataResult {
  const [rows, setRows] = useState<ReportRow[]>([])
  const [status, setStatus] = useState<ReportStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null)
  const [tick, setTick] = useState(0)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const refetch = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    if (!enabled || !reportId) return

    const key = cacheKey(reportId, limit)
    const cached = CACHE.get(key)
    if (!skipCache && cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      setRows(cached.rows)
      setStatus('ok')
      setError(null)
      setFetchedAt(new Date(cached.fetchedAt))
      return
    }

    setStatus('loading')
    setError(null)

    fetchReport(reportId, limit)
      .then((rs) => {
        if (!mounted.current) return
        CACHE.set(key, { rows: rs, fetchedAt: Date.now() })
        setRows(rs)
        setStatus('ok')
        setFetchedAt(new Date())
      })
      .catch((e: unknown) => {
        if (!mounted.current) return
        setError(e instanceof Error ? e.message : String(e))
        setRows([])
        setStatus('error')
      })
  }, [reportId, limit, enabled, skipCache, tick])

  return { rows, status, error, fetchedAt, refetch }
}

/** Prefetch briefing views into cache. */
export function prefetchReports(ids: string[], limit = 500): Promise<Record<string, ReportRow[]>> {
  return Promise.all(
    ids.map(async (id) => {
      try {
        const key = cacheKey(id, limit)
        const cached = CACHE.get(key)
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          return [id, cached.rows] as const
        }
        const rows = await fetchReport(id, limit)
        CACHE.set(key, { rows, fetchedAt: Date.now() })
        return [id, rows] as const
      } catch {
        return [id, [] as ReportRow[]] as const
      }
    }),
  ).then((pairs) => Object.fromEntries(pairs))
}
